import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PackageIcon, SearchIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { object, optional, picklist, string } from 'valibot';
import { useGetRequests } from '@/api/requests/queries';
import { useGetSubscription } from '@/api/subscription/queries';
import { EmptyPlaceholder } from '@/components/empty-placeholder';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UpgradeLink } from '@/components/upgrade-link';
import { cleanObject } from '@/utils';
import { Categories } from './-components/categories';
import { CreateRequestDialog } from './-components/create-request-dialog';
import { RequestSheet } from './-components/request-sheet';
import { RequestsList } from './-components/requests-list';
import { SearchInput } from './-components/search-input';
import { ALL_STATUSES_OPTION, STATUSES } from './-utils/config';

const FREE_PLAN_REQUEST_LIMIT = 15;

const VALID_STATUSES = [ALL_STATUSES_OPTION, ...Object.values(STATUSES)].map(
  ({ status }) => status,
);

const SORT_BY_OPTIONS = [
  { label: 'Newest', value: 'date:desc' },
  { label: 'Oldest', value: 'date:asc' },
  { label: 'Most voted', value: 'votes:desc' },
  { label: 'Least voted', value: 'votes:asc' },
  { label: 'Most commented', value: 'comments:desc' },
  { label: 'Least commented', value: 'comments:asc' },
];

const queryParamsSchema = object({
  status: optional(picklist(VALID_STATUSES), ALL_STATUSES_OPTION.status),
  sortBy: optional(picklist(SORT_BY_OPTIONS.map(({ value }) => value)), SORT_BY_OPTIONS[0].value),
  query: optional(string()),
  requestId: optional(string()),
});

export const Route = createFileRoute('/_authenticated/_projects/projects/$projectId/requests/')({
  component: RequestsComponent,
  loader: () => {
    return {
      crumb: 'Requests',
    };
  },
  validateSearch: queryParamsSchema,
});

function RequestsComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const { data, isPending } = useGetRequests(
    projectId,
    cleanObject({ status: search.status, sortBy: search.sortBy }),
  );
  const { data: subscription } = useGetSubscription();

  const [isRequestDialogOpened, setIsRequestDialogOpened] = useState(!!search.requestId);
  const [selectedRequestId, setSelectedRequestId] = useState(search.requestId || '');

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({ search: (prev) => ({ ...prev, query: value }), params: true });
    },
    [navigate],
  );

  const handleSortByChange = (value: string) => {
    navigate({ search: (prev) => ({ ...prev, sortBy: value }), params: true });
  };

  const handleRequestSheetOpen = (requestId: string) => {
    setIsRequestDialogOpened(true);
    setSelectedRequestId(requestId);
    navigate({ search: (prev) => ({ ...prev, requestId }), params: true });
  };

  const handleRequestSheetClose = () => {
    setIsRequestDialogOpened(false);
    setSelectedRequestId('');
    navigate({ search: (prev) => ({ ...prev, requestId: '' }), params: true });
  };

  const dataToDisplay = (data || []).filter((request) =>
    request.name.toLowerCase().includes(search.query ?? ''),
  );
  const isFreePlan = subscription?.isFree;
  const isRequestLimitReached = isFreePlan && (data?.length ?? 0) >= FREE_PLAN_REQUEST_LIMIT;

  return (
    <>
      <PageContainer>
        <div className="flex w-full flex-col">
          <PageHeader
            title="Feature Requests"
            rightControls={
              isRequestLimitReached ? (
                <div className="flex text-muted-foreground items-baseline text-sm gap-4">
                  You have reached the limit of {FREE_PLAN_REQUEST_LIMIT} requests.
                  <UpgradeLink />
                </div>
              ) : (
                <CreateRequestDialog />
              )
            }
          />
          <div className="mt-2 flex flex-1 flex-col gap-6 sm:flex-row">
            <div className="w-full sm:w-48">
              <Categories projectId={projectId} status={search.status} />
            </div>
            <div className="flex flex-1 flex-col space-y-4">
              <div className="flex gap-3">
                <Select defaultValue={search.sortBy} onValueChange={handleSortByChange}>
                  <SelectTrigger className="w-[204px] justify-start [&_svg]:ml-auto">
                    <span className="text-muted-foreground">Sort:</span>
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {SORT_BY_OPTIONS.map(({ label, value }) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <SearchInput value={search.query || ''} onChange={handleSearchChange} />
              </div>
              {isPending ? null : dataToDisplay?.length ? (
                <RequestsList data={dataToDisplay} onItemClick={handleRequestSheetOpen} />
              ) : (
                <EmptyPlaceholder
                  Icon={search.query ? SearchIcon : PackageIcon}
                  title={
                    search.query
                      ? `Nothing was found for the query "${search.query}"`
                      : 'No requests with this status'
                  }
                  description={search.query ? 'No requests found for the query' : undefined}
                />
              )}
            </div>
          </div>
        </div>
      </PageContainer>
      {isRequestDialogOpened && (
        <RequestSheet
          isOpen={isRequestDialogOpened}
          setIsOpen={handleRequestSheetClose}
          requestId={selectedRequestId}
          onRequestDelete={handleRequestSheetClose}
        />
      )}
    </>
  );
}
